import React, { useEffect, useRef, useState } from "react";
import "./FundConvertApp.css";
import { ApiPaths } from "./../../../Config/ApiPath";
import { BasicInfo, toastFailed, toastSuccess } from "../../../Config/BasicInfo";
import useAxiosHelper from "../../../Common/AxiosHelper";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setIncomeWallet } from "../../../Redux/IncomeWallet";
import moment from "moment";
import {
  ArrowDownUp,
  Wallet,
  Landmark,
  RefreshCcw,
  ShieldCheck,
  AlertTriangle,
  Check,
  X,
  Inbox,
  ChevronRight,
  ShoppingBag,
} from "lucide-react";

/* Backend rules for /main-to-fund-transfer (API/USER/P2P.js):
   minimum $5, converted 1:1, no fee, email OTP required. */
const MIN_CONVERT = 5;
const OTP_ACTION = "fund_convert";
const PERCENT_CHIPS = [25, 50, 75, 100];

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FundConvert = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { AxiosGet, AxiosPost } = useAxiosHelper();
  const incomeData = useSelector((state) => state.incomeData.incomeWallet);

  const [amountInput, setAmountInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpError, setOtpError] = useState("");
  const [converting, setConverting] = useState(false);
  const [done, setDone] = useState(null);
  const [recent, setRecent] = useState(null); // null = loading
  const otpRefs = useRef([]);

  const wallets = Array.isArray(incomeData) ? incomeData : [];
  const main = wallets.find((w) => w?.slug === "main_wallet");
  const fund = wallets.find((w) => w?.slug === "fund_wallet");
  const mainName = main?.name || "Main Wallet";
  const fundName = fund?.name || "Fund Wallet";
  const balance = Number(main?.value) || 0;
  const fundBal = Number(fund?.value) || 0;

  const amount = Number(amountInput);
  const amountError =
    amountInput === ""
      ? ""
      : !(amount > 0)
      ? "Enter a valid amount"
      : amount < MIN_CONVERT
      ? `Minimum conversion is ${money(MIN_CONVERT)}`
      : amount > balance
      ? `Insufficient ${mainName} balance`
      : "";
  const amountValid = amountInput !== "" && !amountError;
  const mainAfter = Math.max(0, balance - (amountValid ? amount : 0));
  const fundAfter = fundBal + (amountValid ? amount : 0);

  // Always refetch on mount — the Redux copy is shared and can be stale.
  useEffect(() => {
    fetchWalletData();
    fetchRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sheets: Escape closes, page behind stays put
  const sheetOpen = showOtp || !!done;
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !converting) {
        setShowOtp(false);
        setDone(null);
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [sheetOpen, converting]);

  useEffect(() => {
    if (showOtp) setTimeout(() => otpRefs.current[0]?.focus(), 150);
  }, [showOtp]);

  async function fetchWalletData() {
    try {
      const response = await AxiosGet(ApiPaths.getWallets);
      // keep the full list — other pages read income wallets from the same store
      if (Array.isArray(response?.wallets)) dispatch(setIncomeWallet(response.wallets));
    } catch (error) {
      console.log("Error fetching wallet data:", error);
    }
  }

  async function fetchRecent() {
    try {
      const q = new URLSearchParams({ page: 1, limit: 5, source: "fund_convert", debit_credit: "credit" });
      const res = await AxiosGet(`${ApiPaths.getPaymentTransaction}?${q.toString()}`);
      setRecent(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      BasicInfo.isDebug && console.log(e);
      setRecent([]);
    }
  }

  const setPercent = (p) => {
    const v = Math.floor(balance * (p / 100) * 100) / 100;
    setAmountInput(v > 0 ? String(v) : "");
  };

  async function requestOtp() {
    if (sending || !amountValid) return;
    try {
      setSending(true);
      await AxiosPost(ApiPaths.sendOTP, { action: OTP_ACTION });
      toastSuccess("OTP sent to your registered email");
      setOtp(Array(6).fill(""));
      setOtpError("");
      setShowOtp(true);
    } catch (error) {
      toastFailed(error?.response?.data?.message || error?.message || "Could not send the code");
    } finally {
      setSending(false);
    }
  }

  async function FundConvertFunc() {
    const code = otp.join("");
    if (code.length !== 6) {
      setOtpError("Enter the 6-digit code from your email");
      return;
    }
    if (converting) return;
    setConverting(true);
    setOtpError("");
    try {
      const response = await AxiosPost(ApiPaths.convertMainToFund, {
        amount,
        otp: code,
        action: OTP_ACTION,
      });
      toastSuccess(response?.message || "Converted successfully");
      setShowOtp(false);
      setDone({ amount, mainAfter, fundAfter });
      setAmountInput("");
      fetchWalletData();
      fetchRecent();
    } catch (error) {
      const raw = error?.response?.data?.message || error?.response?.data?.error_amount || error?.message;
      setOtpError(String(raw || "Something went wrong").replace(/<\/?p>/g, ""));
    } finally {
      setConverting(false);
    }
  }

  const setDigit = (i, v) => {
    setOtpError("");
    const clean = v.replace(/\D/g, "");
    setOtp((d) => {
      const next = [...d];
      if (!clean) {
        next[i] = "";
        return next;
      }
      clean
        .slice(0, 6 - i)
        .split("")
        .forEach((ch, k) => {
          next[i + k] = ch;
        });
      return next;
    });
    if (clean) otpRefs.current[Math.min(i + clean.length, 5)]?.focus();
  };

  return (
    <section className="fxPage">
      {/* ---------- From → To ---------- */}
      <div className="fxHero">
        <div className="fxHeroTop">
          <span className="fxEyebrow">
            <RefreshCcw size={13} /> Convert
          </span>
          <span className="fxRate">1 : 1 · No fee</span>
        </div>

        <div className="fxPair">
          <div className="fxWallet">
            <span className="fxWalletIcon from">
              <Wallet size={18} />
            </span>
            <span className="fxWalletText">
              <small>From</small>
              <b>{mainName}</b>
            </span>
            <span className="fxWalletBal">
              <small>Available</small>
              <b>{money(balance)}</b>
            </span>
          </div>
          <span className="fxSwap" aria-hidden="true">
            <ArrowDownUp size={18} />
          </span>
          <div className="fxWallet">
            <span className="fxWalletIcon to">
              <Landmark size={18} />
            </span>
            <span className="fxWalletText">
              <small>To</small>
              <b>{fundName}</b>
            </span>
            <span className="fxWalletBal">
              <small>Balance</small>
              <b>{money(fundBal)}</b>
            </span>
          </div>
        </div>
      </div>

      {/* ---------- Amount ---------- */}
      <div className={`fxCard ${amountError ? "isError" : ""}`}>
        <div className="fxCardLabel">
          Amount to convert
          <button type="button" onClick={() => setPercent(100)} disabled={balance <= 0}>
            Max {money(balance)}
          </button>
        </div>
        <label className="fxAmount">
          <span>$</span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            placeholder="0.00"
            value={amountInput}
            onChange={(e) => setAmountInput(e.target.value)}
            aria-label="Amount to convert"
          />
          <em>USDT</em>
        </label>
        <div className="fxPcts">
          {PERCENT_CHIPS.map((p) => (
            <button key={p} type="button" onClick={() => setPercent(p)} disabled={balance <= 0}>
              {p === 100 ? "Max" : `${p}%`}
            </button>
          ))}
        </div>
        {amountError ? (
          <p className="fxError">
            <AlertTriangle size={14} /> {amountError}
          </p>
        ) : (
          <p className="fxHint">Minimum {money(MIN_CONVERT)} · credited instantly</p>
        )}
      </div>

      {/* ---------- Result ---------- */}
      <dl className="fxBreak">
        <div>
          <dt>You'll receive</dt>
          <dd className="pos">{amountValid ? money(amount) : "$0.00"}</dd>
        </div>
        <div>
          <dt>{mainName} after</dt>
          <dd>{money(mainAfter)}</dd>
        </div>
        <div>
          <dt>{fundName} after</dt>
          <dd>{money(fundAfter)}</dd>
        </div>
      </dl>

      <div className="fxCtaBar">
        <button type="button" className="fxCta" disabled={!amountValid || sending} onClick={requestOtp}>
          {sending ? <span className="fxSpin" /> : <ShieldCheck size={18} />}
          {sending ? "Sending code…" : amountValid ? `Convert ${money(amount)}` : "Convert"}
        </button>
      </div>

      {/* ---------- Recent conversions ---------- */}
      <div className="fxSection">
        <div className="fxSectionHead">
          <h2>Recent conversions</h2>
          <button type="button" onClick={() => navigate("/transaction_history")}>
            All activity <ChevronRight size={15} />
          </button>
        </div>
        {recent === null ? (
          <div className="fxList">
            {[0, 1].map((i) => (
              <div key={i} className="fxRow fxSkel">
                <span className="a" />
                <span className="b" />
              </div>
            ))}
          </div>
        ) : recent.length === 0 ? (
          <div className="fxEmpty">
            <Inbox size={20} />
            <span>No conversions yet. Funds you convert will show here.</span>
          </div>
        ) : (
          <div className="fxList">
            {recent.map((t, i) => (
              <div key={t?._id || i} className="fxRow">
                <span className="fxRowIcon">
                  <RefreshCcw size={16} />
                </span>
                <span className="fxRowText">
                  <b>
                    {mainName} → {fundName}
                  </b>
                  <small>{t?.time ? moment(t.time).format("DD MMM YYYY, hh:mm A") : "—"}</small>
                </span>
                <b className="fxRowAmt">+{money(t?.amount)}</b>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---------- OTP ---------- */}
      {showOtp && (
        <div className="fxOverlay" onClick={() => !converting && setShowOtp(false)}>
          <div className="fxSheet" role="dialog" aria-modal="true" aria-labelledby="fxOtpTitle" onClick={(e) => e.stopPropagation()}>
            <span className="fxHandle" aria-hidden="true" />
            <button type="button" className="fxClose" onClick={() => setShowOtp(false)} disabled={converting} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="fxOtpTitle">Confirm conversion</h3>
            <p className="fxSheetSub">Enter the 6-digit code sent to your registered email.</p>
            <div className="fxSummary">
              <small>
                {mainName} → {fundName}
              </small>
              <b>{money(amount)}</b>
            </div>
            <div className="fxOtp" role="group" aria-label="Verification code">
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  className={`${d ? "isFilled" : ""} ${otpError ? "isError" : ""}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  maxLength={6}
                  value={d}
                  aria-label={`Digit ${i + 1}`}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
                    if (e.key === "Enter") FundConvertFunc();
                  }}
                  onFocus={(e) => e.target.select()}
                />
              ))}
            </div>
            {otpError && (
              <p className="fxError center">
                <AlertTriangle size={14} /> {otpError}
              </p>
            )}
            <div className="fxSheetActions">
              <button type="button" className="fxBtn ghost" onClick={() => setShowOtp(false)} disabled={converting}>
                Cancel
              </button>
              <button type="button" className="fxBtn primary" onClick={FundConvertFunc} disabled={converting}>
                {converting ? (
                  <>
                    <span className="fxSpin" /> Converting…
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Success ---------- */}
      {done && (
        <div className="fxOverlay" onClick={() => setDone(null)}>
          <div className="fxSheet center" role="dialog" aria-modal="true" aria-labelledby="fxDoneTitle" onClick={(e) => e.stopPropagation()}>
            <span className="fxHandle" aria-hidden="true" />
            <span className="fxDoneIcon">
              <Check size={34} />
            </span>
            <h3 id="fxDoneTitle">{money(done.amount)} converted</h3>
            <p className="fxSheetSub">Now available in your {fundName}.</p>
            <dl className="fxBreak">
              <div>
                <dt>{mainName}</dt>
                <dd>{money(done.mainAfter)}</dd>
              </div>
              <div>
                <dt>{fundName}</dt>
                <dd className="pos">{money(done.fundAfter)}</dd>
              </div>
            </dl>
            <div className="fxSheetActions">
              <button type="button" className="fxBtn ghost" onClick={() => setDone(null)}>
                Done
              </button>
              <button type="button" className="fxBtn primary" onClick={() => navigate("/package")}>
                <ShoppingBag size={16} /> Buy package
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default FundConvert;
